import Api from '@/services/Api'

// Captura movil de cotizaciones (handheld): usa los endpoints existentes de
// pedidos (cotizacion para factura) y de cotizaciones (tipo requerimiento).
// Las llamadas del escaneo usan Api(false) para no mostrar la barra de carga
// en cada lectura.
const URL_PEDIDOS = 'cxc/pedidos/';
const URL_COTIZACIONES = 'cxc/cotizaciones/';

export const TIPO_FACTURA = 'factura';
export const TIPO_REQUERIMIENTO = 'requerimiento';

export default {

    /**
     * Resuelve SKU o barra y devuelve el producto con sus unidades de medida.
     * @param {String} codigo SKU o barra escaneada
     */
    obtenerCodigo(codigo) {
        return Api(false).get(`${URL_COTIZACIONES}obtener_codigo/`, {
            params: { codigo: codigo },
        });
    },

    /**
     * Crea el encabezado segun el tipo.
     * factura: pedido abierto (cxc_pedido_e) que luego genera la cotizacion.
     * requerimiento: cotizacion tipo R (cxc_cotizacion_e).
     */
    crear(tipo, params) {
        if (tipo === TIPO_FACTURA) {
            return Api().post(URL_PEDIDOS, params);
        }
        return Api().post(URL_COTIZACIONES, params);
    },

    obtener(tipo, id, loading = true) {
        if (tipo === TIPO_FACTURA) {
            return Api(loading).get(`${URL_PEDIDOS}${id}`);
        }
        return Api(loading).get(`${URL_COTIZACIONES}${id}/`);
    },

    /**
     * Agrega 1 (o la cantidad indicada) del producto en la unidad de medida elegida.
     */
    agregar(tipo, id, sku, unidadMedida, cantidad = 1) {
        if (tipo === TIPO_FACTURA) {
            return Api(false).post(`${URL_PEDIDOS}${id}/agregar_detalle_um/`, {
                producto: sku,
                unidad_medida: unidadMedida,
                cantidad: cantidad,
            });
        }
        return Api(false).post(`${URL_COTIZACIONES}${id}/agregar_detalle/`, {
            producto: sku,
            unidad_medida: unidadMedida,
            cantidad_um: cantidad,
        });
    },

    /**
     * Cambia la cantidad de una linea (solo cotizacion de requerimiento).
     */
    modificarCantidad(id, lineaId, cantidadUm) {
        return Api(false).post(`${URL_COTIZACIONES}${id}/modificar_detalle/`, {
            id: lineaId,
            cantidad_um: cantidadUm,
        });
    },

    eliminarLinea(tipo, id, lineaId) {
        if (tipo === TIPO_FACTURA) {
            return Api().post(`${URL_PEDIDOS}${id}/eliminar_detalle/`, { id: lineaId });
        }
        return Api().post(`${URL_COTIZACIONES}${id}/eliminar_detalle/`, { id: lineaId });
    },

    /**
     * Finaliza la captura.
     * factura: genera la cotizacion (cierra el pedido).
     * requerimiento: cierra la cotizacion.
     */
    finalizar(tipo, id) {
        if (tipo === TIPO_FACTURA) {
            return Api().post(`${URL_PEDIDOS}${id}/generar_cotizacion/`);
        }
        return Api().post(`${URL_COTIZACIONES}${id}/cerrar/`);
    },

    /**
     * Reabre una cotizacion de requerimiento cerrada (no vencida ni asignada).
     */
    abrir(id) {
        return Api().post(`${URL_COTIZACIONES}${id}/abrir/`);
    },

    /**
     * Anula la cotizacion.
     * factura: anula el pedido (misma accion del modulo de cotizaciones a clientes).
     * requerimiento: anula la cotizacion (no se permite si respalda un requerimiento activo).
     */
    anular(tipo, id) {
        if (tipo === TIPO_FACTURA) {
            return Api().post(`${URL_PEDIDOS}${id}/anular/`);
        }
        return Api().post(`${URL_COTIZACIONES}${id}/anular/`);
    },

    /**
     * Duplica una cotizacion de requerimiento como una nueva (abierta, precios recalculados).
     * Devuelve { cotizacion_e, numero, advertencias }.
     */
    clonar(id) {
        return Api().post(`${URL_COTIZACIONES}${id}/clonar/`);
    },

    /**
     * Busca por tienda y numero cotizaciones de requerimiento de otra tienda para trasladarlas
     * a la tienda de la cotizacion indicada. Cada resultado trae puede_trasladar y motivo.
     * @param {String} id cotizacion desde la que se ejecuta la accion (define la tienda destino)
     */
    buscarParaTraslado(id, tienda, numero) {
        return Api().get(`${URL_COTIZACIONES}${id}/buscar_para_traslado/`, {
            params: { tienda: tienda, numero: numero },
        });
    },

    /**
     * Crea en la tienda de la cotizacion indicada una copia de cotizacionOrigen y anula la original.
     * Devuelve { cotizacion_e, numero, advertencias }.
     */
    trasladarATienda(id, cotizacionOrigen) {
        return Api().post(`${URL_COTIZACIONES}${id}/trasladar_a_tienda/`, {
            cotizacion_origen: cotizacionOrigen,
        });
    },

    /**
     * Desde el listado (sin cotizacion abierta): busca cotizaciones de tiendaOrigen para
     * trasladarlas a tiendaDestino. Cada resultado trae puede_trasladar y motivo.
     */
    buscarTrasladoATienda(tiendaDestino, tiendaOrigen, numero) {
        return Api().get(`${URL_COTIZACIONES}buscar_traslado_a_tienda/`, {
            params: { tienda_destino: tiendaDestino, tienda_origen: tiendaOrigen, numero: numero },
        });
    },

    /**
     * Desde el listado: crea en tiendaDestino una copia de cotizacionOrigen y anula la original.
     * Devuelve { cotizacion_e, numero, advertencias }.
     */
    trasladarATiendaDestino(tiendaDestino, cotizacionOrigen) {
        return Api().post(`${URL_COTIZACIONES}trasladar_a_tienda_destino/`, {
            tienda_destino: tiendaDestino,
            cotizacion_origen: cotizacionOrigen,
        });
    },

    /**
     * Cambia el cliente de una cotizacion abierta.
     * factura: accion cambiar_cliente del pedido (no la usa el POS).
     * requerimiento: actualizacion del encabezado de la cotizacion (mismos campos que el modulo completo);
     * se envia el supercliente y el backend copia nit, nombre y direccion.
     * @param {Object} current encabezado cargado de la cotizacion
     * @param {Object} cliente { codigo, nit, nombre, direccion, email } (codigo solo en requerimiento)
     */
    cambiarCliente(tipo, current, cliente) {
        if (tipo === TIPO_FACTURA) {
            return Api().post(`${URL_PEDIDOS}${current.id}/cambiar_cliente/`, {
                cliente_nit: cliente.nit,
                cliente_nombre: cliente.nombre,
                cliente_direccion: cliente.direccion,
                email: cliente.email || null,
            });
        }
        const datos = {
            super_cliente: cliente.codigo,
            email: cliente.email || null,
        };
        // fecha_vence llega como dd/mm/yyyy y se envia como yyyy-mm-dd para no modificarla
        let fechaVence = current.fecha_vence || null;
        if (fechaVence && fechaVence.indexOf('/') > -1) {
            const partes = fechaVence.split('/');
            fechaVence = `${partes[2]}-${partes[1]}-${partes[0]}`;
        }
        return Api().put(`${URL_COTIZACIONES}${current.id}/`, Object.assign({
            id: current.id,
            tienda: current.tienda,
            fecha_vence: fechaVence,
        }, datos));
    },

    listar(tipo, params) {
        if (tipo === TIPO_FACTURA) {
            return Api().get(URL_PEDIDOS, {
                params: Object.assign({ solo_cot: true }, params),
            });
        }
        return Api().get(URL_COTIZACIONES, { params: params });
    },

    tiendasUsuario() {
        return Api().get('cat/tienda-usuario/', {
            params: { myprofile: true, quitar_paginacion: true },
        });
    },

    cajas(tienda) {
        return Api().get('cxc/caja/', {
            params: { tienda: tienda, quitar_paginacion: true },
        });
    },

    buscarClientes(texto) {
        return Api(false).get('cat/clientes/', {
            params: { page: 1, 'filter[search]': texto },
        });
    },

    crearCliente(params) {
        return Api().post('cat/clientes/', params);
    },

    /**
     * Superclientes creados en la tienda (cotizacion de requerimiento),
     * buscando por codigo, nombre o NIT.
     */
    buscarSuperClientes(texto, tienda) {
        return Api(false).get('cat/super-cliente/', {
            params: { page: 1, tienda_prefijo: tienda, 'filter[search]': texto },
        });
    },

    /**
     * Crea el supercliente en la tienda indicada; el backend genera el codigo CL{tienda}NNNNNN.
     * @param {Object} params { nit, nombre, direccion, tienda }
     */
    crearSuperCliente(params) {
        return Api().post('cat/super-cliente/', params);
    },
}
